// =============================================================================
// BookingPanel — Pannello laterale per creazione appuntamento (sostituisce il modale)
// =============================================================================

import { X, Check, Calendar, Loader2, AlertCircle, RefreshCw, Sparkles, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { useSchedulerActions } from '../../../store/schedulerStore';
import { L } from '../../../config/labels';
import { useBooking } from './BookingContext';
import { CustomerServiceStep } from './CustomerServiceStep';
import { SmartConstraintsStep } from './SmartConstraintsStep';
import { SlotConfirmStep } from './SlotConfirmStep';

// ===========================================================================
// Public entry point
// ===========================================================================

export function BookingPanel() {
  return <BookingPanelShell />;
}

// ===========================================================================
// Panel Shell (header + step indicator + content + footer)
// ===========================================================================

function BookingPanelShell() {
  const { t } = useTranslation('scheduler');
  const {
    state,
    dispatch,
    handleClose,
    handleNextStep,
    handleBackStep,
    handleSubmit,
    handleRequestOptimization,
    createAppointment,
    overrideBooking,
    optimizeSuggestion,
    selectedService,
    slotStart,
    slotEnd,
    refetchSlots,
  } = useBooking();

  const { step, bookingMode, submitError, showOptimizationPanel, showManualTimeInput } = state;

  // ── Continue button disabled logic (step 1) ──────────────────────────
  const isStep1Disabled =
    bookingMode === 'custom'
      ? !state.customEvent.title.trim() ||
        (state.customEvent.requiresClient && !state.selectedCustomerId) ||
        state.customEvent.resourceRequirements.length === 0
      : !state.selectedServiceId ||
        (selectedService?.requiresClient !== false && !state.selectedCustomerId);

  const { setBookingPanelMode } = useSchedulerActions();

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200 shadow-xl">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-emerald-600 to-emerald-700 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white truncate">
            {t('booking.title', { entity: L.appointment })}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setBookingPanelMode('modal')}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition-colors text-white text-xs font-medium"
            title={t('booking.switchToPopup')}
          >
            <Maximize2 className="w-3.5 h-3.5" />
            <span className="hidden xl:inline">Popup</span>
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* ── Step Indicator (compact) ──────────────────────────────── */}
      <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          <StepPill
            number={1}
            label={t('booking.stepCustomerService', { customer: L.customer, service: L.service })}
            active={step === 'customer-service'}
            completed={step === 'smart-constraints' || step === 'slot-confirm'}
          />
          <div className="flex-1 h-px bg-gray-300" />
          <StepPill
            number={2}
            label={t('booking.stepConstraints')}
            active={step === 'smart-constraints'}
            completed={step === 'slot-confirm'}
          />
          <div className="flex-1 h-px bg-gray-300" />
          <StepPill
            number={3}
            label={t('booking.stepTime')}
            active={step === 'slot-confirm'}
            completed={false}
          />
        </div>
      </div>

      {/* ── Content (scrollable) ────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-5">
        {step === 'customer-service' && <CustomerServiceStep />}
        {step === 'smart-constraints' && <SmartConstraintsStep />}
        {step === 'slot-confirm' && <SlotConfirmStep />}
      </div>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        {/* Error display (step 3 only) */}
        {step === 'slot-confirm' && submitError && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 mb-3 rounded text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{submitError}</div>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => {
                      dispatch({ type: 'CLEAR_ERROR' });
                      refetchSlots();
                    }}
                    className="text-xs underline hover:no-underline flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    {t('booking.refreshAvailability')}
                  </button>

                  {(submitError.includes('occupata') ||
                    submitError.includes('conflict') ||
                    showManualTimeInput) &&
                    !showOptimizationPanel && (
                      <button
                        onClick={handleRequestOptimization}
                        disabled={optimizeSuggestion.isPending}
                        className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded hover:bg-purple-200 flex items-center gap-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        {t('booking.tryOptimize')}
                      </button>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center justify-between">
          {step === 'customer-service' ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={handleNextStep}
                disabled={isStep1Disabled}
                className="btn-primary px-5 py-2 text-sm"
              >
                {t('common:buttons.continue')}
              </button>
            </>
          ) : step === 'smart-constraints' ? (
            <>
              <button
                onClick={handleBackStep}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                {t('common:buttons.back')}
              </button>
              <button
                onClick={handleNextStep}
                className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Calendar className="w-4 h-4" />
                {t('booking.searchSlots')}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBackStep}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
              >
                {t('common:buttons.back')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !slotStart ||
                  !slotEnd ||
                  createAppointment.isPending ||
                  overrideBooking.isPending
                }
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
              >
                {createAppointment.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common:status.saving')}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {t('booking.confirmAppointment')}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// StepPill (compact indicator for side panel)
// ===========================================================================

interface StepPillProps {
  number: number;
  label: string;
  active: boolean;
  completed: boolean;
}

function StepPill({ number, label, active, completed }: StepPillProps) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors',
          active && 'bg-emerald-600 text-white',
          completed && 'bg-green-600 text-white',
          !active && !completed && 'bg-gray-200 text-gray-500',
        )}
      >
        {completed ? <Check className="w-3 h-3" /> : number}
      </div>
      <span className={cn('text-xs hidden xl:inline', active ? 'text-gray-900 font-medium' : 'text-gray-500')}>
        {label}
      </span>
    </div>
  );
}

export default BookingPanel;
